import { useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Wallet, Plus, X, RefreshCw, CheckCircle2, Trash2, Mail, Download, Upload, Search, Save } from 'lucide-react'
import {
  listRuns, createRun, getRun, regenerateRun, finalizeRun, deleteRun,
  updatePayslip, emailPayslip, exportRun, importRun, MONTHS,
  type PayrollRun, type Payslip, type PayslipUpdate, type PayrollImportResult,
} from '@/api/payroll'

const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40'
const NOW = new Date()

// Editable per-month columns (in sheet order) and the derived (computed) ones.
type EditField =
  | 'basic' | 'per_hour_rate' | 'overtime_hours' | 'medical_reimbursement' | 'commission'
  | 'adjustment' | 'provident_fund' | 'loan_deducted' | 'loan_deduction_month'
  | 'loan_applied' | 'remaining_loan' | 'food' | 'gym_membership' | 'wht'

function num(v: unknown): number { return Number(v ?? 0) || 0 }

function fmt(v: number): string {
  return v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

// Compute the derived columns from a row's effective values.
function derive(row: Record<string, number>) {
  const overtime_earning = num(row.overtime_hours) * num(row.per_hour_rate)
  const gross = num(row.basic) + overtime_earning + num(row.medical_reimbursement) + num(row.commission) + num(row.adjustment)
  const total_deduction = num(row.provident_fund) + num(row.loan_deducted) + num(row.food) + num(row.gym_membership) + num(row.wht)
  const net = gross - total_deduction
  return { overtime_earning, gross, total_deduction, net }
}

export default function Payroll() {
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({ month: NOW.getMonth() + 1, year: NOW.getFullYear() })
  const [error, setError] = useState('')
  const [banner, setBanner] = useState('')
  const [search, setSearch] = useState('')
  // Per-row dirty edits: { [payslipId]: { field: value } }
  const [drafts, setDrafts] = useState<Record<string, Partial<Record<EditField, number>>>>({})
  const [showExport, setShowExport] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importPreview, setImportPreview] = useState<PayrollImportResult | null>(null)
  const [importError, setImportError] = useState('')

  const { data: runs = [] } = useQuery({ queryKey: ['payroll-runs'], queryFn: listRuns })
  const { data: run } = useQuery({
    queryKey: ['payroll-run', selectedId],
    queryFn: () => getRun(selectedId!),
    enabled: !!selectedId,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['payroll-runs'] })
    if (selectedId) qc.invalidateQueries({ queryKey: ['payroll-run', selectedId] })
  }

  const createMut = useMutation({
    mutationFn: () => createRun(createForm),
    onSuccess: (r) => { invalidate(); setShowCreate(false); setSelectedId(r.id) },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to create run'),
  })
  const regenMut = useMutation({ mutationFn: (id: string) => regenerateRun(id), onSuccess: () => { setDrafts({}); invalidate() } })
  const finalizeMut = useMutation({ mutationFn: (id: string) => finalizeRun(id), onSuccess: invalidate })
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteRun(id),
    onSuccess: () => { setSelectedId(null); invalidate() },
  })
  const emailMut = useMutation({
    mutationFn: (id: string) => emailPayslip(id),
    onSuccess: () => setBanner('Payslip emailed.'),
    onError: (e: any) => setBanner(e?.response?.data?.detail || 'Email failed'),
  })
  const saveMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: PayslipUpdate }) => updatePayslip(id, data),
    onSuccess: (_r, vars) => {
      setDrafts((d) => { const n = { ...d }; delete n[vars.id]; return n })
      invalidate()
    },
    onError: (e: any) => setBanner(e?.response?.data?.detail || 'Save failed'),
  })
  const exportMut = useMutation({
    mutationFn: (format: 'csv' | 'xlsx') => exportRun(run!.id, format, `payroll-${run!.year}-${String(run!.month).padStart(2, '0')}`),
    onSuccess: () => setShowExport(false),
    onError: (e: any) => setBanner(e?.response?.data?.detail || 'Export failed'),
  })

  const closeImport = () => { setImportFile(null); setImportPreview(null); setImportError('') }
  const previewMut = useMutation({
    mutationFn: (file: File) => importRun(run!.id, file, false),
    onSuccess: (r, file) => { setImportError(''); setImportFile(file); setImportPreview(r) },
    onError: (e: any) => { setImportPreview(null); setImportError(e?.response?.data?.detail || 'Could not read the file') },
  })
  const applyImportMut = useMutation({
    mutationFn: () => importRun(run!.id, importFile!, true),
    onSuccess: (r) => {
      setDrafts({})
      invalidate()
      closeImport()
      setBanner(`Imported: ${r.updated} payslip${r.updated === 1 ? '' : 's'} updated, ${r.unchanged} unchanged, ${r.skipped.length} skipped.`)
    },
    onError: (e: any) => setImportError(e?.response?.data?.detail || 'Import failed'),
  })
  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-picking the same file after fixing it
    if (!file) return
    setImportError('')
    setImportFile(file)
    setImportPreview(null)
    previewMut.mutate(file)
  }

  const isDraft = run?.status === 'draft'

  const setField = (id: string, field: EditField, value: number) =>
    setDrafts((d) => ({ ...d, [id]: { ...d[id], [field]: value } }))

  const effective = (p: Payslip): Record<string, number> => {
    const d = drafts[p.id] || {}
    const row: Record<string, number> = {}
    const fields: EditField[] = ['basic', 'per_hour_rate', 'overtime_hours', 'medical_reimbursement', 'commission', 'adjustment', 'provident_fund', 'loan_deducted', 'loan_deduction_month', 'loan_applied', 'remaining_loan', 'food', 'gym_membership', 'wht']
    for (const f of fields) row[f] = d[f] ?? num((p as any)[f])
    return row
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const slips = run?.payslips || []
    if (!q) return slips
    return slips.filter((p: Payslip) =>
      `${p.user_name || ''} ${p.employee_id || ''} ${p.designation || ''}`.toLowerCase().includes(q))
  }, [run, search])

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Wallet className="h-6 w-6 text-[#EC5B2C]" /> Payroll</h1>
          <p className="text-sm text-gray-500">Monthly salary sheet — fill in each employee's figures, calculations update live.</p>
        </div>
        <button onClick={() => { setError(''); setShowCreate(true) }} className="flex items-center gap-2 rounded-lg bg-[#EC5B2C] px-4 py-2 text-sm font-medium text-white"><Plus className="h-4 w-4" /> New Run</button>
      </div>

      {banner && <div className="mb-4 rounded-lg bg-brand/10 border border-brand/20 text-brand-dark text-sm px-3 py-2 flex justify-between"><span>{banner}</span><button onClick={() => setBanner('')}><X className="h-4 w-4" /></button></div>}

      <div className="grid lg:grid-cols-[240px_1fr] gap-6">
        {/* Runs list */}
        <div className="space-y-2">
          {runs.length === 0 && <p className="text-sm text-gray-400">No payroll runs yet.</p>}
          {runs.map((r: PayrollRun) => (
            <button
              key={r.id}
              onClick={() => { setSelectedId(r.id); setSearch(''); setDrafts({}) }}
              className={`w-full text-left rounded-lg border p-3 transition-colors ${selectedId === r.id ? 'border-[#EC5B2C] bg-[#EC5B2C]/5' : 'border-gray-200 bg-white hover:border-gray-300'}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">{MONTHS[r.month]} {r.year}</span>
                <StatusBadge status={r.status} />
              </div>
              <p className="text-xs text-gray-500 mt-1">{r.payslip_count} payslips · {money(r.total_net)}</p>
            </button>
          ))}
        </div>

        {/* Run detail */}
        <div className="min-w-0">
          {!run ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400 text-sm">Select a run to view the salary sheet.</div>
          ) : (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{MONTHS[run.month]} {run.year}</h2>
                  <p className="text-sm text-gray-500">{run.payslip_count} employees · Total net {money(run.total_net)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <div className="relative">
                    <button onClick={() => setShowExport((s) => !s)} className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700"><Download className="h-4 w-4" /> Export</button>
                    {showExport && (
                      <div className="absolute right-0 mt-1 w-32 rounded-lg border border-gray-200 bg-white shadow-lg z-10 text-sm">
                        <button onClick={() => exportMut.mutate('xlsx')} className="block w-full text-left px-3 py-2 hover:bg-gray-50">Excel (.xlsx)</button>
                        <button onClick={() => exportMut.mutate('csv')} className="block w-full text-left px-3 py-2 hover:bg-gray-50">CSV (.csv)</button>
                      </div>
                    )}
                  </div>
                  {isDraft && (
                    <>
                      <input ref={fileInputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onPickFile} />
                      <button onClick={() => fileInputRef.current?.click()} disabled={previewMut.isPending} className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-60"><Upload className="h-4 w-4" /> {previewMut.isPending ? 'Reading…' : 'Import'}</button>
                    </>
                  )}
                  {isDraft && <button onClick={() => { if (confirm('Regenerate wipes all entered figures for this run. Continue?')) regenMut.mutate(run.id) }} disabled={regenMut.isPending} className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700"><RefreshCw className="h-4 w-4" /> Regenerate</button>}
                  {isDraft && <button onClick={() => { if (confirm('Finalize this run? Figures can no longer be edited.')) finalizeMut.mutate(run.id) }} disabled={finalizeMut.isPending} className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white"><CheckCircle2 className="h-4 w-4" /> Finalize</button>}
                  {isDraft && <button onClick={() => { if (confirm('Delete this draft run?')) deleteMut.mutate(run.id) }} className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-600"><Trash2 className="h-4 w-4" /> Delete</button>}
                </div>
              </div>

              <div className="relative mb-3 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee…" className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40" />
              </div>

              <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                <table className="text-sm whitespace-nowrap">
                  <thead className="bg-gray-50 text-gray-500 text-left sticky top-0">
                    <tr>
                      <Th className="sticky left-0 bg-gray-50 z-10">Employee</Th>
                      <Th>Designation</Th>
                      <Th right>Basic</Th>
                      <Th right>Per Hr</Th>
                      <Th right>OT Hrs</Th>
                      <Th right>OT Earning</Th>
                      <Th right>Medical</Th>
                      <Th right>Commission</Th>
                      <Th right>Adjustment</Th>
                      <Th right>Gross</Th>
                      <Th right>PF</Th>
                      <Th right>Loan Ded.</Th>
                      <Th right>Loan/Mo</Th>
                      <Th right>Loan App.</Th>
                      <Th right>Rem. Loan</Th>
                      <Th right>Food</Th>
                      <Th right>Gym</Th>
                      <Th right>WHT</Th>
                      <Th right>Total Ded.</Th>
                      <Th right>Net Pay</Th>
                      <Th right>Actions</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.length === 0 ? (
                      <tr><td colSpan={21} className="px-4 py-8 text-center text-gray-400">No payslips — set salaries for employees, then Regenerate.</td></tr>
                    ) : filtered.map((p: Payslip) => {
                      const row = effective(p)
                      const d = derive(row)
                      const dirty = !!drafts[p.id] && Object.keys(drafts[p.id]).length > 0
                      const cur = p.currency
                      const numCell = (field: EditField) => (
                        isDraft ? (
                          <td key={field} className="px-1.5 py-1 text-right">
                            <input
                              type="number" step="any"
                              value={row[field] === 0 ? '' : row[field]}
                              placeholder="0"
                              onChange={(e) => setField(p.id, field, Number(e.target.value) || 0)}
                              className="w-20 rounded border border-gray-200 px-1.5 py-1 text-right text-xs focus:outline-none focus:ring-1 focus:ring-[#EC5B2C]/50"
                            />
                          </td>
                        ) : (
                          <td key={field} className="px-3 py-2 text-right text-gray-600">{fmt(num((p as any)[field]))}</td>
                        )
                      )
                      return (
                        <tr key={p.id} className={`hover:bg-gray-50 ${dirty ? 'bg-amber-50/40' : ''}`}>
                          <td className="px-3 py-2 sticky left-0 bg-white z-10">
                            <div className="font-medium text-gray-900">{p.user_name}</div>
                            <div className="text-xs text-gray-400">{p.employee_id || '—'} · {p.present_days}/{p.working_days} days</div>
                          </td>
                          <td className="px-3 py-2 text-gray-600">{p.designation || '—'}</td>
                          {numCell('basic')}
                          {numCell('per_hour_rate')}
                          {numCell('overtime_hours')}
                          <td className="px-3 py-2 text-right text-gray-500">{fmt(d.overtime_earning)}</td>
                          {numCell('medical_reimbursement')}
                          {numCell('commission')}
                          {numCell('adjustment')}
                          <td className="px-3 py-2 text-right font-medium text-gray-800">{fmt(d.gross)}</td>
                          {numCell('provident_fund')}
                          {numCell('loan_deducted')}
                          {numCell('loan_deduction_month')}
                          {numCell('loan_applied')}
                          {numCell('remaining_loan')}
                          {numCell('food')}
                          {numCell('gym_membership')}
                          {numCell('wht')}
                          <td className="px-3 py-2 text-right text-gray-500">{fmt(d.total_deduction)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-900">{money(d.net, cur)}</td>
                          <td className="px-2 py-2">
                            <div className="flex items-center justify-end gap-1">
                              {isDraft && dirty && (
                                <button title="Save" onClick={() => saveMut.mutate({ id: p.id, data: drafts[p.id] as PayslipUpdate })} disabled={saveMut.isPending} className="p-1.5 rounded bg-[#EC5B2C] text-white"><Save className="h-4 w-4" /></button>
                              )}
                              <button title="Email payslip" onClick={() => emailMut.mutate(p.id)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><Mail className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {isDraft && <p className="text-xs text-gray-400 mt-2">Edited rows are highlighted — click the save icon to store each row. Overtime Earning, Gross, Total Deduction and Net are calculated automatically. You can also Export, edit in Excel/Sheets (formulas included) and Import the file back.</p>}
            </div>
          )}
        </div>
      </div>

      {/* Import preview modal */}
      {(importPreview || importError) && (
        <Modal title="Import salary sheet" onClose={closeImport} wide>
          {importFile && <p className="text-xs text-gray-500 mb-3">{importFile.name}</p>}
          {importError && <div className="mb-3 rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{importError}</div>}
          {importPreview && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="rounded-full bg-green-100 text-green-700 px-3 py-1">{importPreview.updated} to update</span>
                <span className="rounded-full bg-gray-100 text-gray-600 px-3 py-1">{importPreview.unchanged} unchanged</span>
                <span className="rounded-full bg-amber-100 text-amber-700 px-3 py-1">{importPreview.skipped.length} skipped</span>
                <span className={`rounded-full px-3 py-1 ${importPreview.errors.length ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{importPreview.errors.length} errors</span>
              </div>
              <p className="text-xs text-gray-500">Name and designation are never changed. Gross, deductions and net are recalculated from the imported figures.</p>

              {importPreview.errors.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 max-h-40 overflow-y-auto">
                  <div className="font-medium mb-1">Fix these in the sheet and upload again:</div>
                  {importPreview.errors.map((x, i) => <div key={i}>Row {x.row} ({x.employee_id || '—'}): {x.message}</div>)}
                </div>
              )}
              {importPreview.skipped.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 max-h-32 overflow-y-auto">
                  <div className="font-medium mb-1">Skipped rows:</div>
                  {importPreview.skipped.map((x, i) => <div key={i}>Row {x.row} ({x.employee_id || '—'}): {x.message}</div>)}
                </div>
              )}
              {importPreview.rows.length > 0 && (
                <div className="rounded-lg border border-gray-200 max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-left sticky top-0">
                      <tr><th className="px-3 py-2 font-medium">Employee</th><th className="px-3 py-2 font-medium">Changes</th><th className="px-3 py-2 font-medium text-right">Net</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {importPreview.rows.map((r) => (
                        <tr key={r.payslip_id} className="align-top">
                          <td className="px-3 py-2"><div className="font-medium text-gray-900">{r.user_name}</div><div className="text-xs text-gray-400">{r.employee_id}</div></td>
                          <td className="px-3 py-2 text-xs text-gray-600">{r.changes.map((c) => <div key={c.field}>{c.label}: {fmt(num(c.old))} → <span className="font-medium text-gray-900">{fmt(num(c.new))}</span></div>)}</td>
                          <td className="px-3 py-2 text-right text-xs whitespace-nowrap">{fmt(num(r.net_before))} → <span className="font-semibold text-gray-900">{fmt(num(r.net_after))}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {importPreview.rows.length === 0 && importPreview.errors.length === 0 && <p className="text-sm text-gray-500">No figures differ from the current run — nothing to import.</p>}

              <div className="flex justify-end gap-2">
                <button onClick={closeImport} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600">Cancel</button>
                <button
                  onClick={() => applyImportMut.mutate()}
                  disabled={applyImportMut.isPending || importPreview.errors.length > 0 || importPreview.updated === 0}
                  className="px-4 py-2 text-sm rounded-lg bg-[#EC5B2C] text-white disabled:opacity-50"
                >
                  {applyImportMut.isPending ? 'Applying…' : `Apply ${importPreview.updated} change${importPreview.updated === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Create run modal */}
      {showCreate && (
        <Modal title="New Payroll Run" onClose={() => setShowCreate(false)}>
          {error && <div className="mb-3 rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{error}</div>}
          <label className="block mb-3">
            <span className="block text-xs font-medium text-gray-600 mb-1">Month</span>
            <select className={inputCls} value={createForm.month} onChange={(e) => setCreateForm({ ...createForm, month: Number(e.target.value) })}>
              {MONTHS.slice(1).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </label>
          <label className="block mb-4">
            <span className="block text-xs font-medium text-gray-600 mb-1">Year</span>
            <input type="number" className={inputCls} value={createForm.year} onChange={(e) => setCreateForm({ ...createForm, year: Number(e.target.value) })} />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600">Cancel</button>
            <button onClick={() => createMut.mutate()} disabled={createMut.isPending} className="px-4 py-2 text-sm rounded-lg bg-[#EC5B2C] text-white disabled:opacity-60">Generate</button>
          </div>
        </Modal>
      )}
    </div>
  )
}

function Th({ children, right, className = '' }: { children: React.ReactNode; right?: boolean; className?: string }) {
  return <th className={`px-3 py-2.5 font-medium ${right ? 'text-right' : ''} ${className}`}>{children}</th>
}

function money(v: number | string | null | undefined, currency = 'PKR'): string {
  const n = Number(v ?? 0)
  return `${currency} ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'finalized' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ${cls}`}>{status}</span>
}

function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-xl w-full ${wide ? 'max-w-3xl' : 'max-w-md'}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div className="font-semibold text-gray-900">{title}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}
